import { useState, useEffect } from "react";
import { cmsApi } from "../services/cmsApi";
import { LandingMenuResponse } from "../types/menu";

export const MenuSection = () => {
  const [menuData, setMenuData] = useState<LandingMenuResponse | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const data = await cmsApi.getLandingMenu();
        setMenuData(data);
        const firstCategory = Object.keys(data.menu_by_category)[0];
        if (firstCategory) {
          setActiveCategory(firstCategory);
        }
      } catch (err) {
        setError("Error al cargar el menú");
        console.error("Error fetching menu:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMenu();
  }, []);

  if (isLoading) {
    return (
      <section id="menu" className="py-12 bg-salbox-neutral-50 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-salbox-primary mx-auto"></div>
            <p className="mt-4 text-salbox-neutral-600">Cargando menú...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !menuData) {
    return (
      <section id="menu" className="py-12 bg-salbox-neutral-50 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-red-600">{error || "Error al cargar el menú"}</p>
          </div>
        </div>
      </section>
    );
  }

  const categories = Object.keys(menuData.menu_by_category);
  const activeMenuCategory = menuData.menu_by_category[activeCategory];

  return (
    <section id="menu" className="py-12 bg-salbox-primary scroll-mt-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-lufga-semibold text-white mb-4 uppercase">
            Nuestro Menú
          </h2>
          <p className="text-lg font-lufga text-white/90 max-w-2xl mx-auto">
            Descubre nuestra deliciosa variedad de platillos preparados con
            ingredientes frescos y amor.
          </p>
        </div>

        {/* Tabs de categorías con scroll horizontal */}
        <div className="mb-8">
          <div className="flex gap-3 overflow-x-auto pb-2 px-4 md:justify-center scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {categories.map((categoryName) => {
              const category = menuData.menu_by_category[categoryName];
              return (
                <button
                  key={categoryName}
                  onClick={() => setActiveCategory(categoryName)}
                  className={`flex items-center gap-3 px-5 py-3 rounded-full font-lufga-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                    activeCategory === categoryName
                      ? "bg-white text-salbox-primary shadow-lg border-2 border-white"
                      : "bg-white/80 text-salbox-neutral-700 hover:bg-white hover:shadow-md border border-white/50"
                  }`}
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-salbox-neutral-100 flex-shrink-0">
                    {category.category_image_url ? (
                      <img
                        src={category.category_image_url}
                        alt={categoryName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-xs">🍴</span>
                      </div>
                    )}
                  </div>
                  {categoryName}
                </button>
              );
            })}
          </div>
        </div>

        {/* Platillos de la categoría activa */}
        {activeMenuCategory && (
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {activeMenuCategory.dishes.map((dish) => (
                <div
                  key={dish.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
                >
                  <div className="h-32 md:h-48 overflow-hidden bg-salbox-neutral-100">
                    {dish.image_url ? (
                      <img
                        src={dish.image_url}
                        alt={dish.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-4xl mb-2">🍽️</div>
                          <span className="text-salbox-neutral-400 text-sm font-lufga">
                            Sin imagen
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-4 md:p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-lufga-semibold text-sm md:text-lg text-salbox-neutral-800 flex-1">
                        {dish.name}
                      </h3>
                      <span className="text-base md:text-lg font-lufga-bold text-salbox-primary ml-2">
                        ${dish.display_price}
                      </span>
                    </div>

                    <p className="text-salbox-neutral-600 font-lufga text-xs md:text-sm leading-relaxed">
                      {dish.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
